// src/components/layout/AppShell.tsx
import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { useTheme } from "@/lib/theme";
import { markNotificationsRead } from "@/lib/firestore";
import { Avatar, toast } from "@/components/shared";
import { formatDistanceToNow } from "date-fns";
import type { Ticket } from "@/types";

// agentOnly entries are hidden from regular users. Dashboard and All
// Tickets are queue-wide views a user has no read access to (Firestore
// rules scope them to their own tickets), so their nav starts at My
// Tickets. The Support section is identical for every role.
const NAV = [
  { to: "/",          label: "Dashboard",      shortLabel: "Home",     icon: "M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z",        section: "overview", agentOnly: true },
  { to: "/tickets",   label: "All Tickets",    shortLabel: "Tickets",  icon: "M22 10V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v4c1.1 0 2 .9 2 2s-.9 2-2 2v4c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-4c-1.1 0-2-.9-2-2s.9-2 2-2z", section: "overview", badge: "tickets", agentOnly: true },
  { to: "/my-tickets",label: "My Tickets",     shortLabel: "Mine",     icon: "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z", section: "overview" },
  { to: "/chat",      label: "AI Assistant",   shortLabel: "AI",       icon: "M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z",  section: "support", aiTag: true },
  { to: "/kb",        label: "Knowledge Base", shortLabel: "KB",       icon: "M17.5 12c-2.5 0-4.5 2-4.5 4.5S15 21 17.5 21s4.5-2 4.5-4.5S20 12 17.5 12zm.5 7l-1.75-1.75L17 16l1 1 2.25-2.25.75.75L18 19zm-6 1H5c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2h14c1.1 0 2 .9 2 2v7.5c-.8-.5-1.6-.75-2.5-.75V5H5v14h7v1z", section: "support" },
  { to: "/analytics", label: "Analytics",      shortLabel: "Analytics",icon: "M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z", section: "admin" },
  { to: "/settings",  label: "Settings",       shortLabel: "Settings", icon: "M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z", section: "admin" },
];

// Below this width we collapse the sidebar, show a bottom tab bar with
// the 5 main entries, and stash Analytics/Settings/Sign Out behind a cog
// dropdown next to the user avatar.
const MOBILE_QUERY = "(max-width: 767px)";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

export default function AppShell() {
  const { helpDeskUser, firebaseUser, signOut } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const isAgent = helpDeskUser?.role === "agent" || helpDeskUser?.role === "admin";

  const isMobile = useIsMobile();
  const theme = useTheme();
  const [recentTickets, setRecentTickets] = useState<Ticket[]>([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [showCogMenu, setShowCogMenu] = useState(false);
  const [localLastReadMs, setLocalLastReadMs] = useState<number | null>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const cogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!firebaseUser) return;
    const q = isAgent
      ? query(collection(db, "tickets"), orderBy("updatedAt", "desc"), limit(8))
      : query(collection(db, "tickets"),
          where("submitterId", "==", firebaseUser.uid),
          orderBy("updatedAt", "desc"),
          limit(8));
    return onSnapshot(q, (snap) => {
      setRecentTickets(snap.docs.map((d) => ({ id: d.id, ...d.data() } as Ticket)));
    });
  }, [firebaseUser, isAgent]);

  useEffect(() => {
    if (!showNotifs) return;
    const onClick = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setShowNotifs(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showNotifs]);

  useEffect(() => {
    if (!showCogMenu) return;
    const onClick = (e: MouseEvent) => {
      if (cogRef.current && !cogRef.current.contains(e.target as Node)) setShowCogMenu(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showCogMenu]);

  // Unread = ticket updates more recent than the user's last "mark read" click.
  // Brand-new accounts that have never clicked: count anything updated in the
  // last 24h so the bell doesn't immediately show every existing ticket as new.
  const lastReadMs = localLastReadMs
    ?? helpDeskUser?.notificationsLastReadAt?.toMillis()
    ?? (Date.now() - 24 * 60 * 60 * 1000);
  const unreadCount = recentTickets.filter((t) => {
    if (!t.updatedAt) return false;
    return t.updatedAt.toMillis() > lastReadMs;
  }).length;

  const handleMarkAllRead = async () => {
    if (!firebaseUser) return;
    setLocalLastReadMs(Date.now()); // optimistic — clears the dot immediately
    try {
      await markNotificationsRead(firebaseUser.uid);
    } catch (e) {
      toast(`Could not mark as read: ${(e as Error).message}`, "error");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* Header */}
      <header style={{
        height: "var(--hdr-h)", background: "var(--header-bg)",
        backdropFilter: "blur(20px)", borderBottom: "0.5px solid var(--border)",
        display: "flex", alignItems: "center", padding: "0 16px", gap: 10,
        flexShrink: 0, zIndex: 20,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 15, letterSpacing: "-0.3px" }}>
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt={branding.portalName} style={{ height: 28, maxWidth: 120, objectFit: "contain" }} />
          ) : (
            <div style={{ width: 28, height: 28, background: "var(--blue)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="white">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
            </div>
          )}
          {branding.portalName}
        </div>

        <div style={{ flex: 1 }} />

        <button
          className="btn btn-primary btn-sm"
          onClick={() => navigate("/tickets/new")}
          title="New ticket"
        >
          {isMobile ? "+" : "+ New Ticket"}
        </button>

        {/* Theme toggle (sun ↔ moon) */}
        <button
          className="btn btn-secondary btn-icon btn-sm"
          onClick={theme.toggle}
          title={theme.isDark ? "Switch to light mode" : "Switch to dark mode"}
          aria-label={theme.isDark ? "Switch to light mode" : "Switch to dark mode"}
        >
          {theme.isDark ? (
            // Sun icon — shown when in dark mode (clicking goes to light)
            <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--text2)">
              <path d="M6.76 4.84l-1.8-1.79-1.41 1.41 1.79 1.79 1.42-1.41zM4 10.5H1v2h3v-2zm9-9.95h-2V3.5h2V.55zm7.45 3.91l-1.41-1.41-1.79 1.79 1.41 1.41 1.79-1.79zm-3.21 13.7l1.79 1.8 1.41-1.41-1.8-1.79-1.4 1.4zM20 10.5v2h3v-2h-3zm-8-5c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm-1 16.95h2V19.5h-2v2.95zm-7.45-3.91l1.41 1.41 1.79-1.8-1.41-1.41-1.79 1.8z"/>
            </svg>
          ) : (
            // Moon icon — shown in light mode (clicking goes to dark)
            <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--text2)">
              <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z"/>
            </svg>
          )}
        </button>

        {/* Notifications */}
        <div ref={notifRef} style={{ position: "relative" }}>
          <button
            type="button"
            className="btn btn-secondary btn-icon btn-sm"
            style={{ position: "relative" }}
            onClick={() => setShowNotifs(!showNotifs)}
            aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : "Notifications"}
            aria-haspopup="true"
            aria-expanded={showNotifs}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--text2)" aria-hidden="true">
              <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
            </svg>
            {unreadCount > 0 && (
              <span aria-hidden="true" style={{ position: "absolute", top: 6, right: 6, width: 7, height: 7, background: "var(--red)", borderRadius: "50%", border: "1.5px solid white" }} />
            )}
          </button>
          {showNotifs && (
            <div className="card" style={{
              position: "absolute", top: "calc(100% + 8px)", right: 0, width: 340,
              maxHeight: 420, overflow: "auto", boxShadow: "var(--shadow-md)", zIndex: 50,
            }}>
              <div style={{ padding: "10px 14px", borderBottom: "0.5px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Recent activity</span>
                {unreadCount > 0 && (
                  <button onClick={handleMarkAllRead}
                    style={{ background: "none", border: "none", color: "var(--blue)", fontSize: 12, cursor: "pointer", padding: 0, fontFamily: "var(--font)" }}>
                    Mark all read
                  </button>
                )}
              </div>
              {recentTickets.length === 0 ? (
                <div style={{ padding: 20, textAlign: "center", fontSize: 12, color: "var(--text2)" }}>
                  No recent ticket activity.
                </div>
              ) : (
                recentTickets.map((t) => (
                  <div key={t.id} onClick={() => { navigate(`/tickets/${t.id}`); setShowNotifs(false); }}
                    style={{ padding: "10px 14px", borderBottom: "0.5px solid var(--border)", cursor: "pointer", fontSize: 13 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--gray)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                    <div style={{ fontWeight: 500, marginBottom: 2 }}>{t.title}</div>
                    <div style={{ fontSize: 11, color: "var(--text2)" }}>
                      {t.status} · {t.priority} · {t.updatedAt ? formatDistanceToNow(t.updatedAt.toDate(), { addSuffix: true }) : ""}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Cog dropdown — mobile only. Carries Analytics/Settings/Sign Out
            since the sidebar is collapsed on small screens. */}
        {isMobile && (
          <div ref={cogRef} style={{ position: "relative" }}>
            <button
              className="btn btn-secondary btn-icon btn-sm"
              onClick={() => setShowCogMenu(!showCogMenu)}
              title="Menu"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--text2)">
                <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
              </svg>
            </button>
            {showCogMenu && (
              <div className="card" style={{
                position: "absolute", top: "calc(100% + 8px)", right: 0, width: 200,
                boxShadow: "var(--shadow-md)", zIndex: 50, padding: "6px 0",
              }}>
                <CogItem label="Preferences" onClick={() => { navigate("/preferences"); setShowCogMenu(false); }} icon="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                {isAgent && (
                  <>
                    <CogItem label="Analytics" onClick={() => { navigate("/analytics"); setShowCogMenu(false); }} icon="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
                    <CogItem label="Settings" onClick={() => { navigate("/settings"); setShowCogMenu(false); }} icon="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
                  </>
                )}
                <div style={{ borderTop: "0.5px solid var(--border)", margin: "4px 0" }} />
                <div style={{ padding: "8px 14px 4px" }}>
                  <div style={{ fontSize: 12, fontWeight: 500 }}>{helpDeskUser?.displayName ?? "User"}</div>
                  <div style={{ fontSize: 11, color: "var(--text2)" }}>{helpDeskUser?.role}</div>
                </div>
                <CogItem label="Sign Out" onClick={() => { setShowCogMenu(false); signOut(); }} icon="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
              </div>
            )}
          </div>
        )}

        {/* Profile — goes to personal preferences (available to every role);
            the admin console lives in the sidebar. On mobile the cog handles it. */}
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: isMobile ? "default" : "pointer" }}
          onClick={isMobile ? undefined : () => navigate("/preferences")}
          title={isMobile ? undefined : "Preferences"}
        >
          <Avatar name={helpDeskUser?.displayName ?? firebaseUser?.displayName ?? "User"} url={helpDeskUser?.avatarUrl ?? firebaseUser?.photoURL ?? undefined} size={28} />
        </div>
      </header>

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Sidebar — hidden on mobile (bottom nav + cog menu cover its job) */}
        {!isMobile && (
        <nav aria-label="Main navigation" style={{ width: "var(--sidebar-w)", background: "var(--surface)", borderRight: "0.5px solid var(--border)", display: "flex", flexDirection: "column", flexShrink: 0, overflowY: "auto", padding: "8px 0" }}>

          {["overview", "support", "admin"].map((section) => {
            const items = NAV.filter((n) => n.section === section
              && (section !== "admin" || isAgent)
              && (!n.agentOnly || isAgent));
            if (!items.length) return null;
            return (
              <div key={section} style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", letterSpacing: ".6px", textTransform: "uppercase", padding: "0 14px 4px", marginTop: 8 }}>
                  {section === "overview" ? "Overview" : section === "support" ? "Support" : "Admin"}
                </div>
                {items.map((item) => (
                  <NavLink key={item.to} to={item.to} end={item.to === "/"} style={{ textDecoration: "none" }}>
                    {({ isActive }) => (
                      <div style={{
                        display: "flex", alignItems: "center", gap: 9,
                        padding: "7px 14px", fontSize: 13.5,
                        color: isActive ? "var(--blue-t)" : "var(--text)",
                        background: isActive ? "var(--blue-l)" : "transparent",
                        fontWeight: isActive ? 500 : 400,
                        transition: "background 0.1s",
                      }}
                        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "var(--gray)"; }}
                        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill={isActive ? "var(--blue)" : "var(--text2)"}>
                          <path d={item.icon} />
                        </svg>
                        {item.label}
                        {item.aiTag && (
                          <span aria-hidden="true" style={{ marginLeft: "auto", background: "var(--gem)", color: "white", fontSize: 9, padding: "1px 5px", borderRadius: 8, fontWeight: 700 }}>AI</span>
                        )}
                      </div>
                    )}
                  </NavLink>
                ))}
              </div>
            );
          })}

          <div style={{ marginTop: "auto", borderTop: "0.5px solid var(--border)", padding: "10px 14px" }}>
            <div
              onClick={() => navigate("/preferences")}
              title="Preferences"
              style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer" }}
            >
              <Avatar name={helpDeskUser?.displayName ?? "User"} url={helpDeskUser?.avatarUrl ?? undefined} size={24} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{helpDeskUser?.displayName ?? "User"}</div>
                <div style={{ fontSize: 11, color: "var(--text2)" }}>{helpDeskUser?.role}</div>
              </div>
            </div>
            <button type="button" className="btn btn-secondary btn-sm" style={{ width: "100%", justifyContent: "center", marginBottom: 6 }} onClick={() => navigate("/preferences")}>
              Preferences
            </button>
            <button type="button" className="btn btn-secondary btn-sm" style={{ width: "100%", justifyContent: "center" }} onClick={signOut}>
              Sign Out
            </button>
          </div>
        </nav>
        )}

        {/* Main content */}
        <main id="main-content" style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", paddingBottom: isMobile ? 56 : 0 }}>
          <Outlet />
        </main>
      </div>

      {/* Bottom nav — mobile only */}
      {isMobile && (
        <nav aria-label="Primary" style={{
          position: "fixed", bottom: 0, left: 0, right: 0, height: 56,
          background: "var(--bottom-nav-bg)", backdropFilter: "blur(20px)",
          borderTop: "0.5px solid var(--border)",
          display: "flex", alignItems: "stretch",
          zIndex: 30,
          paddingBottom: "env(safe-area-inset-bottom, 0)",
        }}>
          {NAV.filter((n) => n.section !== "admin" && (!n.agentOnly || isAgent)).map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} style={{ flex: 1, textDecoration: "none" }}>
              {({ isActive }) => (
                <div style={{
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  gap: 2, height: "100%",
                  color: isActive ? "var(--blue)" : "var(--text2)",
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d={item.icon} />
                  </svg>
                  <span style={{ fontSize: 10, fontWeight: isActive ? 600 : 500, letterSpacing: 0.1 }}>
                    {item.shortLabel}
                  </span>
                </div>
              )}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}

function CogItem({ label, onClick, icon }: { label: string; onClick: () => void; icon: string }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "8px 14px", fontSize: 13, cursor: "pointer",
        color: "var(--text)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--gray)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="var(--text2)">
        <path d={icon} />
      </svg>
      {label}
    </div>
  );
}
